"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import { useShowFollowingWaves } from "../../../../hooks/useShowFollowingWaves";
import { useAuth } from "../../../auth/Auth";

// Pure helper function for authentication state logic (testable)
const checkConnectedIdentity = (connectedHandle: string | null | undefined, activeProfileProxy: unknown): boolean => {
  return !!connectedHandle && !activeProfileProxy;
};

/**
 * WavesFilterToggle component that renders an app-like button group to filter waves.
 * Shows "All" and "Joined" options in a segmented control style.
 * Only renders when the user is authenticated with a connected identity (not using a proxy).
 * Uses localStorage to persist the toggle state across sessions.
 * 
 * @returns JSX element containing the button group toggle, or null if user is not authenticated or on error
 */
const WavesFilterToggle = () => {
  const $ = _c(17);
  const followingHookResult = useShowFollowingWaves();
  const authResult = useAuth();
  let t0;
  if ($[0] !== followingHookResult) {
    t0 = followingHookResult || [false, _temp];
    $[0] = followingHookResult;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [following, setFollowing] = t0;
  let t1;
  if ($[2] !== authResult) {
    t1 = authResult || {};
    $[2] = authResult;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    connectedProfile,
    activeProfileProxy
  } = t1;
  const connectedHandle = connectedProfile?.handle;
  const isConnectedIdentity = checkConnectedIdentity(connectedHandle, activeProfileProxy);
  if (!followingHookResult) {
    console.warn("[WavesFilterToggle] useShowFollowingWaves hook failed - component will not render", {
      component: "WavesFilterToggle",
      error: "hook_failure",
      hook: "useShowFollowingWaves"
    });
    return null;
  }
  if (!isConnectedIdentity) {
    return null;
  }
  const getButtonClassName = _temp2;
  ;
  try {
    const t3 = getButtonClassName(!following);
    let t4;
    if ($[4] !== setFollowing) {
      t4 = () => setFollowing(false);
      $[4] = setFollowing;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== t3 || $[7] !== t4) {
      t5 = <button className={t3} onClick={t4}>All</button>;
      $[6] = t3;
      $[7] = t4;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    const t6 = getButtonClassName(following);
    let t7;
    if ($[9] !== setFollowing) {
      t7 = () => setFollowing(true);
      $[9] = setFollowing;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== t6 || $[12] !== t7) {
      t8 = <button className={t6} onClick={t7}>Joined</button>;
      $[11] = t6;
      $[12] = t7;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    let t9;
    if ($[14] !== t5 || $[15] !== t8) {
      t9 = <div className="tw-flex tw-items-center tw-whitespace-nowrap tw-h-8 tw-px-1 tw-text-xs tw-border tw-border-iron-700 tw-border-solid tw-rounded-lg tw-overflow-hidden tw-bg-iron-950">{t5}{t8}</div>;
      $[14] = t5;
      $[15] = t8;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    return t9;
  } catch (t2) {
    const error = t2;
    console.warn("[WavesFilterToggle] Rendering error occurred", {
      component: "WavesFilterToggle",
      error: "rendering_failure",
      errorMessage: error instanceof Error ? error.message : String(error),
      following,
      connectedHandle: !!connectedHandle
    });
    return null;
  }
};
WavesFilterToggle.displayName = "WavesFilterToggle";
export default WavesFilterToggle;
function _temp() {}
function _temp2(isActive) {
  if (isActive) {
    return "tw-px-2.5 tw-py-1 tw-border-0 tw-rounded-md tw-transition tw-duration-300 tw-ease-out tw-text-xs tw-font-medium tw-bg-iron-800 tw-text-iron-50";
  }
  return "tw-px-2.5 tw-py-1 tw-border-0 tw-rounded-md tw-transition tw-duration-300 tw-ease-out tw-text-xs tw-font-medium tw-text-iron-400 desktop-hover:hover:tw-text-iron-300 tw-bg-iron-950";
}